import { formatLimitValue } from '../api/pricingApprovals';

import type { Approval } from './approvalsDemo';
import type { ApprovalRequest, LimitViolation } from '../api/pricingApprovals';

const KIND: Record<LimitViolation['kind'], string> = {
	below_floor: 'below the floor',
	above_ceiling: 'above the ceiling',
	outside_rep_range: 'outside rep range',
};

const RATE: Record<LimitViolation['rate'], string> = {
	discount_rate: 'Discount rate',
	authorization_fee: 'Authorization fee',
	monthly_fee: 'Monthly fees',
	batch_fee: 'Batch fee',
};

const APPROVER = {
	pricing: 'Pricing',
	manager: 'Manager',
	admin: 'Admin',
} as const;

const when = (iso: string) => new Date(iso).toLocaleString();

const signed = (v: LimitViolation, diff: number) =>
	`${diff < 0 ? '−' : '+'}${formatLimitValue(v.rate, Math.abs(diff))}`;

// One API approval request as a row of the approval queue.
export const toApproval = (r: ApprovalRequest, now = Date.now()): Approval => {
	const v = r.violations[0];
	const limitsBroken = r.violations
		.map(
			(x) =>
				`${RATE[x.rate]} ${formatLimitValue(x.rate, x.proposed)} ${KIND[x.kind]} ${formatLimitValue(x.rate, x.limit)}`,
		)
		.join('; ');
	const reason = r.request_reason
		? `${limitsBroken}. Requester: “${r.request_reason}”`
		: limitsBroken;
	const chain = [
		{ label: 'Quote edited', detail: limitsBroken, when: when(r.requested_at) },
		{
			label: 'Approval requested',
			detail: r.request_reason
				? `By ${r.requested_by_name}: ${r.request_reason}`
				: `By ${r.requested_by_name}`,
			when: when(r.requested_at),
		},
	];
	if (r.decided_at) {
		chain.push({
			label: `Decision: ${r.status.replace('_', ' ')}`,
			detail: r.decision_note ?? `By ${r.decided_by_name}`,
			when: when(r.decided_at),
		});
	}
	return {
		id: r.id.slice(0, 8).toUpperCase(),
		requestId: r.id,
		merchant: r.merchant_name,
		template: r.template_name ?? 'No template',
		partner: '—',
		type: 'Quote',
		requestedBy: r.requested_by_name,
		proposed: v ? formatLimitValue(v.rate, v.proposed) : null,
		floor: v?.floor != null ? formatLimitValue(v.rate, v.floor) : null,
		vsFloor: v?.floor != null ? signed(v, v.proposed - v.floor) : null,
		belowFloor: r.violations.some((x) => x.kind === 'below_floor'),
		note: v && v.kind !== 'below_floor' ? KIND[v.kind] : undefined,
		ageHours: Math.max(
			0,
			Math.floor((now - Date.parse(r.requested_at)) / 3_600_000),
		),
		status: r.status,
		reason,
		approver:
			r.decided_by_name ??
			(r.required_approver
				? `${APPROVER[r.required_approver]} or above`
				: 'Any admin'),
		chain,
	};
};
